import {
  Accordion,
  Alert,
  Button,
  DEFAULT_INPUT_HEIGHT,
  DescriptionList,
  FlexSpacer,
  FormRow,
  Icon,
  Input,
  Link,
  Paragraph,
  Skeleton,
  Stack,
  Text,
  Title,
  Wizard,
  useWizardPage
} from '@efast_public/fjd-component-library';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Trans, useTranslation } from 'react-i18next';
import { useNotification } from '../../../../shared/notification/useNotification';
import { WeiterleitungsWizardState } from '../../../../modals/ReceiverSettingsWizard/Wizard.types';
import {
  ClientDestinationsModel,
  DestinationSources
} from '../../../../api/hooks/useDestinations';
import { BehApiRoutes } from '../../../../api/beh-api';
import { AxiosError } from 'axios';
import {
  validateClientDestinationsModel,
  ValidationError
} from '../../../shared/validation/ClientDestinationModelsValidation';
import { v4 as uuidv4 } from 'uuid';
import {
  FJD_DOCS_EINRICHTUNGSASSISTANT,
  FJD_DOCS_SETTINGS_FITCONNECT
} from '../../../../shared/utils/Links';

// TODO: Build duplicate name validation with joi and useForm

function DestinationRenaming({ isSettings }: { isSettings?: boolean }) {
  const { t } = useTranslation();
  const { notifyUser } = useNotification();

  const [orgUnits, setOrgUnits] = useState<ClientDestinationsModel>();

  const {
    error: destinationsFromDBError,
    isLoading: isValidating,
    data: destinationsFromDB,
    refetch: refetchDestinations
  } = useQuery<ClientDestinationsModel>({
    queryKey: [BehApiRoutes.destinations(DestinationSources.fitconnect)]
  });

  const validationErrors =
    !isValidating && orgUnits
      ? validateClientDestinationsModel(orgUnits)
      : new Map<string, Array<ValidationError>>();

  const onSubmit = useCallback(async () => {
    if (orgUnits) {
      setWizardState((prevState) => ({
        ...prevState,
        state: {
          ...(prevState.state as WeiterleitungsWizardState),
          orgUnits
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnits]);
  const { wizardState, setWizardState } = useWizardPage({
    isFormValid: !isValidating && validationErrors.size === 0,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    onSubmit
  });

  useEffect(() => {
    const savedOrgUnits = (
      wizardState.state as WeiterleitungsWizardState | undefined
    )?.orgUnits;
    if (savedOrgUnits) {
      setOrgUnits(savedOrgUnits);
      return;
    }

    if (
      !isValidating &&
      destinationsFromDB &&
      destinationsFromDB.organizationalUnitDtos.length > 0
    ) {
      setOrgUnits(destinationsFromDB);
    } else if (
      !isValidating &&
      (destinationsFromDB?.organizationalUnitDtos.length ?? 0) === 0
    ) {
      setWizardState((prevState) => ({
        ...prevState,
        skipPageWithIndex: [
          ...(prevState.skipPageWithIndex ?? []),
          prevState.currentPageIndex + 1
        ]
      }));
    } else if (
      !isValidating &&
      (destinationsFromDBError?.cause as AxiosError).status === 401
    ) {
      notifyUser({
        intent: 'error',
        title: t('api.error.fetchDestination.unauthorizied')
      });
      setWizardState((prevState) => ({
        ...prevState,
        currentPageIndex: prevState.currentPageIndex - 1
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationsFromDB, isValidating]);

  return (
    <Wizard.Page>
      <Stack spacing="m">
        {destinationsFromDBError &&
          !isValidating &&
          (destinationsFromDBError.cause as AxiosError)?.status !== 404 && (
            <Alert
              variant={'error'}
              message={
                'FIT-Connect ist momentan nicht erreichbar, daher können die Zustellpunkte nicht abgerufen werden. Bitte versuchen Sie es später erneut.'
              }
            />
          )}
        <Title level={2} visualLevel={6}>
          {t('configAssistent.destinationRenaming.headline', {
            amount: destinationsFromDB?.organizationalUnitDtos?.length
          })}
        </Title>
        <Paragraph>
          {t('configAssistent.destinationRenaming.descriptionText')}
          <Trans
            i18nKey="configAssistent.configAssistentDocumentationNotice"
            components={{
              Link: (
                <Link
                  href={
                    isSettings
                      ? FJD_DOCS_SETTINGS_FITCONNECT
                      : FJD_DOCS_EINRICHTUNGSASSISTANT
                  }
                  target="_blank"
                >
                  hier
                </Link>
              )
            }}
          />
        </Paragraph>
        {!isValidating && validationErrors.size > 0 && (
          <Button
            beforeIcon="restart"
            variant="primary-text"
            onClick={() => refetchDestinations()}
          >
            {t('destinationRenamingModal.resyncWithSSP')}
          </Button>
        )}
        <Title level={2} visualLevel={6}>{`Zustellpunkte`}</Title>
        <Accordion.Group>
          {isValidating &&
            ['1', '2', '3'].map((key) => (
              <Skeleton key={key} height={DEFAULT_INPUT_HEIGHT} />
            ))}
          {orgUnits &&
            orgUnits.organizationalUnitDtos &&
            orgUnits.organizationalUnitDtos.map((orgUnit, index) => (
              <Accordion
                key={orgUnit.destinationDtos[0].destinationId}
                before={
                  <Stack spacing="s" direction="row">
                    {validationErrors.get(
                      orgUnit.destinationDtos[0].destinationId
                    )?.length ? (
                      <Icon size="lg" name="close-filled" variant="error" />
                    ) : (
                      <Icon
                        size="lg"
                        name="checkmark-filled"
                        variant="success"
                      />
                    )}
                    <Text strong>{`${orgUnit.name}`}</Text>
                  </Stack>
                }
              >
                <Stack spacing="s">
                  <FlexSpacer />
                  <DescriptionList>
                    <DescriptionList.Item
                      key={'destinationId'}
                      description={`${orgUnit.destinationDtos[0].destinationId}`}
                      title="Destination-ID"
                    />
                    <DescriptionList.Item
                      key={'nameInFitConnect'}
                      description={`${orgUnit.nameInFitConnect}`}
                      title={
                        <Text strong>
                          Bezeichnung in
                          <br /> FIT-Connect
                        </Text>
                      }
                    />
                  </DescriptionList>
                  <FlexSpacer />
                  <FormRow
                    label="Bezeichnung im Behörden-Client"
                    id={`label${orgUnit.destinationDtos[0].destinationId}`}
                  >
                    <Stack spacing="xs" direction="column">
                      <Input
                        id={`input_${orgUnit.destinationDtos[0].destinationId}`}
                        type="text"
                        value={orgUnit.name}
                        onChange={(event) => {
                          setOrgUnits({
                            organizationalUnitDtos:
                              orgUnits?.organizationalUnitDtos.map(
                                (org, orgIndex) =>
                                  orgIndex === index
                                    ? { ...org, name: event.target.value }
                                    : org
                              )
                          });
                        }}
                      />
                      <Button
                        size="sm"
                        variant="primary-text"
                        beforeIcon="restart"
                        onClick={() => {
                          setOrgUnits({
                            organizationalUnitDtos:
                              orgUnits?.organizationalUnitDtos.map(
                                (org, orgIndex) =>
                                  orgIndex === index
                                    ? {
                                        ...org,
                                        name: org.nameInFitConnect
                                      }
                                    : org
                              )
                          });
                        }}
                      >
                        Auf Name aus FIT-Connect zurücksetzen
                      </Button>
                    </Stack>
                  </FormRow>
                  <Stack spacing="xs">
                    {destinationsFromDB &&
                      validationErrors &&
                      validationErrors.size > 0 &&
                      (() =>
                        validationErrors.get(
                          orgUnit.destinationDtos[0].destinationId
                        ) ?? [])().map((error) => {
                        const errorMessage = t(
                          `destinationRenamingModal.${error.key}`,
                          error.params
                        );
                        return (
                          <Alert
                            key={uuidv4()}
                            message={
                              <div style={{ whiteSpace: 'pre-line' }}>
                                {errorMessage}
                              </div>
                            }
                            variant="error"
                          />
                        );
                      })}
                  </Stack>
                </Stack>
              </Accordion>
            ))}
        </Accordion.Group>
      </Stack>
    </Wizard.Page>
  );
}

export default DestinationRenaming;
